import React, { useState } from 'react';
import { Pencil, Trash2, ChevronRight } from 'lucide-react';
import { designTokens } from '../styles/design-tokens';
import { useTheme } from '../context/ThemeContext';
import type { SavedLocation } from '../lib/api';

interface SavedLocationsSectionProps {
  locations: SavedLocation[];
  loading: boolean;
  visible: boolean;
  onToggleVisible: () => void;
  onClickLocation: (loc: SavedLocation) => void;
  onRename: (id: number, name: string) => Promise<unknown>;
  onDelete: (id: number) => Promise<void>;
}

function subtitleFor(loc: SavedLocation): string {
  if (loc.source_type === 'parcel') return `Parcel · ${loc.source_id ?? ''}`.trim();
  if (loc.source_type === 'well') return `Well · ${loc.source_id ?? ''}`.trim();
  return `${loc.lat.toFixed(4)}, ${loc.lng.toFixed(4)}`;
}

export const SavedLocationsSection: React.FC<SavedLocationsSectionProps> = ({
  locations,
  loading,
  visible,
  onToggleVisible,
  onClickLocation,
  onRename,
  onDelete,
}) => {
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState('');

  const beginEdit = (loc: SavedLocation) => {
    setEditingId(loc.id);
    setEditingValue(loc.name);
  };

  const commitEdit = async () => {
    if (editingId == null) return;
    const trimmed = editingValue.trim();
    if (trimmed) {
      try {
        await onRename(editingId, trimmed);
      } catch (e) {
        console.error('[SavedLocationsSection] rename failed:', e);
      }
    }
    setEditingId(null);
    setEditingValue('');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingValue('');
  };

  const handleDelete = async (loc: SavedLocation) => {
    const ok = window.confirm(`Delete "${loc.name}"?`);
    if (!ok) return;
    try {
      await onDelete(loc.id);
    } catch (e) {
      console.error('[SavedLocationsSection] delete failed:', e);
    }
  };

  return (
    <div>
      {/* Section header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          marginBottom: '8px',
          marginTop: '14px',
        }}
      >
        <button
          onClick={() => setCollapsed(!collapsed)}
          style={{
            display: 'flex',
            alignItems: 'center',
            flex: 1,
            fontSize: '10px',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color: theme.textSecondary,
            whiteSpace: 'nowrap',
            borderLeft: `3px solid ${designTokens.colors.brand}`,
            paddingLeft: '8px',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            borderLeftWidth: '3px',
            borderLeftStyle: 'solid',
            borderLeftColor: designTokens.colors.brand,
          }}
        >
          <ChevronRight
            size={10}
            style={{
              transition: 'transform 0.15s',
              transform: collapsed ? 'rotate(0deg)' : 'rotate(90deg)',
              marginRight: '4px',
              flexShrink: 0,
            }}
          />
          Saved Locations
          <span style={{ marginLeft: 'auto', fontSize: '9px', fontWeight: 400, color: theme.textTertiary }}>
            {locations.length}
          </span>
        </button>
        <button
          onClick={e => {
            e.stopPropagation();
            onToggleVisible();
          }}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '0 12px 0 8px',
            fontSize: '10px',
            fontWeight: 500,
            letterSpacing: '1px',
            color: visible ? designTokens.colors.brand : theme.textTertiary,
            fontFamily: designTokens.typography.fontFamily.mono,
          }}
        >
          {visible ? '[ON]' : '[--]'}
        </button>
      </div>

      {/* List body */}
      {!collapsed && (
        <div style={{ maxHeight: '240px', overflowY: 'auto', marginBottom: '8px' }}>
          {loading && locations.length === 0 && (
            <div style={{ padding: '8px', fontSize: '11px', color: theme.textTertiary }}>Loading…</div>
          )}
          {!loading && locations.length === 0 && (
            <div
              style={{
                padding: '8px',
                fontSize: '11px',
                color: theme.textTertiary,
                lineHeight: 1.4,
              }}
            >
              No saved locations yet. Right-click the map or use the ☆ button on any detail panel.
            </div>
          )}
          {locations.map(loc => (
            <div
              key={loc.id}
              className="saved-location-row"
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '6px 8px',
                borderBottom: `1px solid ${theme.border}`,
                cursor: 'pointer',
                gap: '6px',
              }}
              onClick={() => editingId !== loc.id && onClickLocation(loc)}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                {editingId === loc.id ? (
                  <input
                    autoFocus
                    value={editingValue}
                    onChange={e => setEditingValue(e.target.value)}
                    onClick={e => e.stopPropagation()}
                    onKeyDown={e => {
                      if (e.key === 'Enter') commitEdit();
                      if (e.key === 'Escape') cancelEdit();
                    }}
                    onBlur={commitEdit}
                    style={{
                      width: '100%',
                      padding: '3px 6px',
                      fontSize: '12px',
                      border: `1px solid ${theme.border}`,
                      borderRadius: '3px',
                      background: theme.inputBg,
                      color: theme.text,
                      boxSizing: 'border-box',
                    }}
                  />
                ) : (
                  <>
                    <div
                      style={{
                        fontSize: '12px',
                        fontWeight: 600,
                        color: theme.text,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {loc.name}
                    </div>
                    <div
                      style={{
                        fontSize: '9px',
                        color: theme.textTertiary,
                        fontFamily: designTokens.typography.fontFamily.mono,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {subtitleFor(loc)}
                    </div>
                  </>
                )}
              </div>
              {editingId !== loc.id && (
                <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      beginEdit(loc);
                    }}
                    title="Rename"
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '2px',
                      color: theme.textTertiary,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Pencil size={12} />
                  </button>
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      handleDelete(loc);
                    }}
                    title="Delete"
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '2px',
                      color: theme.textTertiary,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
